import React, { useMemo, useState } from "react"
import type { CompanySummary, SeriesSummary } from "~/api/portfolio_summaries"
import SegToggle from "./SegToggle"
import {
  formatCurrency,
  formatIrr,
  formatTvpi,
  colorClassFor,
  widthClassFor,
} from "./utils"

interface TopListProps {
  companies: CompanySummary[]
  series: SeriesSummary[]
  periodLabel: string
}

type Entity = "company" | "series"
type Basis = "mv" | "cost"
type Count = 10 | 20

const monogram = (name: string) =>
  name
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase()

const moicClass = (moic: number | null) => {
  if (moic == null) return ""
  if (moic >= 1.8) return "hi"
  if (moic >= 1.2) return "mid"
  return "lo"
}

const irrClass = (irr: number | null) => {
  if (irr == null) return ""
  if (irr >= 15) return "hi"
  if (irr >= 5) return "mid"
  return "lo"
}

const TopList: React.FC<TopListProps> = ({ companies, series, periodLabel }) => {
  const [entity, setEntity] = useState<Entity>("company")
  const [basis, setBasis] = useState<Basis>("mv")
  const [count, setCount] = useState<Count>(10)

  const totalCompanyPl = useMemo(
    () => companies.reduce((sum, c) => sum + c.pl, 0),
    [companies],
  )

  const rankedCompanies = useMemo(
    () =>
      [...companies].sort((a, b) =>
        basis === "mv" ? b.mv - a.mv : b.cost - a.cost,
      ),
    [companies, basis],
  )
  const rankedSeries = useMemo(
    () =>
      [...series].sort((a, b) =>
        basis === "mv" ? b.mv - a.mv : b.cost - a.cost,
      ),
    [series, basis],
  )

  const maxCompanyVal = Math.max(
    1,
    ...rankedCompanies.map((c) => (basis === "mv" ? c.mv : c.cost)),
  )
  const maxSeriesVal = Math.max(
    1,
    ...rankedSeries.map((s) => (basis === "mv" ? s.mv : s.cost)),
  )

  return (
    <div className="b-portfolio-summary-card">
      <div className="b-portfolio-summary-card-header">
        <div>
          <h2>
            Top {entity === "company" ? "portfolio companies" : "series"} by{" "}
            {basis === "mv" ? "MV" : "cost"} exposure
          </h2>
          <div className="b-portfolio-summary-card-sub">
            {entity === "company"
              ? `Look-through across all series · ${periodLabel}`
              : `Fund-investment level · ${periodLabel}`}
          </div>
        </div>
        <div className="b-portfolio-summary-toplist-controls">
          <SegToggle
            options={[
              { key: "company", label: "Companies" },
              { key: "series", label: "Series" },
            ]}
            active={entity}
            onChange={(value) => setEntity(value as Entity)}
          />
          <SegToggle
            options={[
              { key: "mv", label: "MV" },
              { key: "cost", label: "Cost" },
            ]}
            active={basis}
            onChange={(value) => setBasis(value as Basis)}
          />
          <SegToggle
            options={[
              { key: "10", label: "Top 10" },
              { key: "20", label: "Top 20" },
            ]}
            active={String(count) as "10" | "20"}
            onChange={(v) => setCount(Number(v) as Count)}
          />
        </div>
      </div>

      <div className="b-portfolio-summary-pc-list">
        {entity === "company" ? (
          <>
            <div className="b-portfolio-summary-pc-row b-portfolio-summary-pc-row--head">
              <div>#</div>
              <div />
              <div>Company</div>
              <div className="r">{basis === "mv" ? "MV exposure" : "Cost exposure"}</div>
              <div className="r">% of P&amp;L</div>
              <div className="r">MOIC</div>
              <div className="r col-hide">% portfolio</div>
            </div>
            {rankedCompanies.length === 0 && (
              <div className="b-portfolio-summary-pc-empty">
                No portfolio-company-level look-through data available for
                this period (requires an uploaded, parsed financial
                statement per series).
              </div>
            )}
            {rankedCompanies.slice(0, count).map((c, i) => {
              const val = basis === "mv" ? c.mv : c.cost
              const pctPl = totalCompanyPl !== 0 ? (c.pl / totalCompanyPl) * 100 : null
              return (
                <div className="b-portfolio-summary-pc-row" key={c.id}>
                  <div className="b-portfolio-summary-pc-rank">{i + 1}</div>
                  <div
                    className={`b-portfolio-summary-pc-logo ${colorClassFor(i)}`}
                  >
                    {monogram(c.name)}
                  </div>
                  <div>
                    <div className="b-portfolio-summary-pc-name">{c.name}</div>
                    {c.description && (
                      <div className="b-portfolio-summary-pc-desc">
                        {c.description}
                      </div>
                    )}
                    <div className="b-portfolio-summary-pc-meta">
                      <span className="chip series-chip">
                        {c.primary_series}
                        {c.extra_series_count > 0 && (
                          <span className="more"> +{c.extra_series_count}</span>
                        )}
                      </span>
                      {c.sector && <span className="chip">{c.sector}</span>}
                      {c.region && <span className="chip">{c.region}</span>}
                    </div>
                  </div>
                  <div className="b-portfolio-summary-pc-num">
                    {formatCurrency(val)}
                    <div className="bar-track">
                      <div
                        className={`bar-mini ${colorClassFor(i)} ${widthClassFor(
                          (val / maxCompanyVal) * 100,
                        )}`}
                      />
                    </div>
                  </div>
                  <div className="b-portfolio-summary-pc-num">
                    {pctPl == null ? "—" : `${pctPl >= 0 ? "" : "−"}${Math.abs(pctPl).toFixed(1)}%`}
                  </div>
                  <div className="b-portfolio-summary-pc-num">
                    <span className={`moic ${moicClass(c.moic)}`}>
                      {formatTvpi(c.moic)}
                    </span>
                  </div>
                  <div className="b-portfolio-summary-pc-num col-hide">
                    {c.pct_of_portfolio == null ? "—" : `${c.pct_of_portfolio.toFixed(1)}%`}
                  </div>
                </div>
              )
            })}
          </>
        ) : (
          <>
            <div className="b-portfolio-summary-pc-row b-portfolio-summary-pc-row--head">
              <div>#</div>
              <div />
              <div>Series</div>
              <div className="r">{basis === "mv" ? "MV exposure" : "Cost exposure"}</div>
              <div className="r">IRR</div>
              <div className="r">MOIC</div>
              <div className="r col-hide">% portfolio</div>
            </div>
            {rankedSeries.slice(0, count).map((s, i) => {
              const val = basis === "mv" ? s.mv : s.cost
              return (
                <div className="b-portfolio-summary-pc-row" key={s.id}>
                  <div className="b-portfolio-summary-pc-rank">{i + 1}</div>
                  <div
                    className={`b-portfolio-summary-pc-logo ${colorClassFor(i)}`}
                  >
                    {monogram(s.name)}
                  </div>
                  <div>
                    <div className="b-portfolio-summary-pc-name">{s.name}</div>
                    <div className="b-portfolio-summary-pc-meta">
                      <span className="chip">Called {formatCurrency(s.cost)}</span>
                      <span className="chip">ITD P&amp;L {formatCurrency(s.itd_pl)}</span>
                      {s.asset_class_label && <span className="chip">{s.asset_class_label}</span>}
                    </div>
                  </div>
                  <div className="b-portfolio-summary-pc-num">
                    {formatCurrency(val)}
                    <div className="bar-track">
                      <div
                        className={`bar-mini ${colorClassFor(i)} ${widthClassFor(
                          (val / maxSeriesVal) * 100,
                        )}`}
                      />
                    </div>
                  </div>
                  <div className="b-portfolio-summary-pc-num">
                    <span className={`moic ${irrClass(s.irr)}`}>{formatIrr(s.irr)}</span>
                  </div>
                  <div className="b-portfolio-summary-pc-num">
                    <span className={`moic ${moicClass(s.moic)}`}>{formatTvpi(s.moic)}</span>
                  </div>
                  <div className="b-portfolio-summary-pc-num col-hide">
                    {s.pct_of_portfolio == null ? "—" : `${s.pct_of_portfolio.toFixed(1)}%`}
                  </div>
                </div>
              )
            })}
          </>
        )}
      </div>
    </div>
  )
}

export default TopList
